import {
  type ButtonHTMLAttributes,
  forwardRef,
  type ReactNode,
  type Ref,
  useRef,
  useState,
} from "react";

import {
  Box,
  Button,
  chakra,
  ChevronDownIcon,
  Combobox,
  type ComboboxProps,
  type ComboboxTriggerComponentProps,
  Row,
  useMergeRefs,
} from "@hightouch/htui";

/** "pill" draws a border, for property rows; "ghost" is bare, for dense lists; "field" fills its row like a form input. */
export type Appearance = "pill" | "ghost" | "field";

type Props<Option, Value extends Option[keyof Option]> = Omit<
  ComboboxProps<Option, Value>,
  "trigger" | "renderInputInControl" | "size" | "width"
> & {
  /** What the closed picker shows, such as an icon and the current value. */
  children: ReactNode;
  "aria-label": string;
  /** "row" fills the width of a list, as its last row. */
  appearance?: Appearance | "row";
  /** "circle" rounds a "pill" button fully, as the page's Add buttons are. */
  shape?: "default" | "circle";
  /** Also gets the picker's button, such as for a popover that opens from a pick. */
  triggerRef?: Ref<HTMLButtonElement>;
};

/**
 * A Linear-style property control: a compact button that opens a searchable
 * list. The list itself is htui's Combobox.
 */
export function PropertyPicker<Option, Value extends Option[keyof Option]>({
  children,
  appearance = "pill",
  shape,
  "aria-label": ariaLabel,
  popoverWidth = "2xs",
  onChange,
  onCreateOption,
  triggerRef,
  ...props
}: Props<Option, Value>) {
  // Without a value, a pick is an action, such as adding a tag. The combobox
  // keeps the picked option internally and reports it again as its options
  // change, so it starts fresh after each pick.
  const isAction = props.value === undefined;
  const [pickCount, setPickCount] = useState(0);
  // It can also report one pick twice before it remounts, so a repeat is dropped.
  const lastPick = useRef<{ count: number; value: Value | undefined }>();
  const afterPick = () => {
    if (isAction) setPickCount((count) => count + 1);
  };

  return (
    <Combobox
      key={pickCount}
      {...props}
      onChange={(value) => {
        if (
          isAction &&
          lastPick.current?.count === pickCount &&
          lastPick.current.value === value
        ) {
          return;
        }
        lastPick.current = { count: pickCount, value };
        onChange?.(value);
        afterPick();
      }}
      onCreateOption={
        onCreateOption &&
        (async (name) => {
          await onCreateOption(name);
          afterPick();
        })
      }
      popoverWidth={popoverWidth}
      renderInputInControl={false}
      size="sm"
      trigger={{
        type: "component",
        component: ({
          isLoading: _isLoading,
          ...triggerProps
        }: ComboboxTriggerComponentProps) => (
          <PickerButton
            {...triggerProps}
            aria-label={ariaLabel}
            appearance={appearance}
            shape={shape}
            triggerRef={triggerRef}
          >
            {children}
          </PickerButton>
        ),
      }}
    />
  );
}

type PickerButtonProps = Omit<
  ComboboxTriggerComponentProps,
  "isLoading" | "ref"
> & {
  appearance: Appearance | "row";
  shape?: "default" | "circle";
  triggerRef?: Ref<HTMLButtonElement>;
  children: ReactNode;
};

const PickerButton = forwardRef<HTMLButtonElement, PickerButtonProps>(
  ({ appearance, shape, triggerRef, ...props }, ref) => {
    const refs = useMergeRefs(ref, triggerRef);
    return appearance === "row" ? (
      <RowButton ref={refs} {...props} />
    ) : (
      <PropertyButton
        ref={refs}
        appearance={appearance}
        shape={shape}
        {...props}
      />
    );
  },
);
PickerButton.displayName = "PickerButton";

type PropertyButtonProps = Omit<
  ButtonHTMLAttributes<HTMLButtonElement>,
  "className" | "style" | "disabled"
> & {
  appearance?: Appearance;
  isDisabled?: boolean;
  /** For the "field" appearance: leaves out the chevron. */
  hideChevron?: boolean;
  /** For the "pill" and "ghost" appearances. */
  shape?: "default" | "circle";
  children: ReactNode;
};

export const PropertyButton = forwardRef<
  HTMLButtonElement,
  PropertyButtonProps
>(({ appearance = "pill", hideChevron, shape, children, ...props }, ref) =>
  appearance === "field" ? (
    <FieldButton ref={ref} hideChevron={hideChevron} {...props}>
      {children}
    </FieldButton>
  ) : (
    <Button
      ref={ref}
      variant={appearance === "pill" ? "secondary" : "tertiary"}
      size="sm"
      shape={shape}
      {...props}
    >
      <Row align="center" gap={1.5} minWidth={0} fontWeight="normal">
        {children}
      </Row>
    </Button>
  ),
);
PropertyButton.displayName = "PropertyButton";

type FieldButtonProps = Omit<
  ButtonHTMLAttributes<HTMLButtonElement>,
  "className" | "style" | "disabled"
> & {
  isDisabled?: boolean;
  /** Leaves out the chevron, for a control that opens a calendar rather than a list. */
  hideChevron?: boolean;
  children: ReactNode;
};

/** A property control drawn as a full-width input, for a details form. */
export const FieldButton = forwardRef<HTMLButtonElement, FieldButtonProps>(
  ({ isDisabled, hideChevron = false, children, ...props }, ref) => (
    <chakra.button
      ref={ref}
      type="button"
      disabled={isDisabled}
      {...props}
      display="flex"
      alignItems="center"
      gap={2}
      width="100%"
      minWidth={0}
      minHeight={8}
      px={2}
      py={1.5}
      bg="base.lightBackground"
      borderWidth="hairline"
      borderColor="base.border"
      borderRadius="lg"
      textAlign="left"
      transitionProperty="border-color"
      transitionDuration="fast"
      transitionTimingFunction="easeOutQuad"
      _hover={{ borderColor: "base.borderHover" }}
      _focusVisible={{ outline: "none", boxShadow: "outline" }}
      _disabled={{ color: "text.disabled", cursor: "not-allowed" }}
    >
      <Row align="center" gap={1.5} flex={1} minWidth={0}>
        {children}
      </Row>
      {!hideChevron && (
        <Box
          as={ChevronDownIcon}
          aria-hidden
          boxSize={5}
          flexShrink={0}
          color="text.secondary"
        />
      )}
    </chakra.button>
  ),
);
FieldButton.displayName = "FieldButton";

type RowButtonProps = Omit<
  ComboboxTriggerComponentProps,
  "isLoading" | "ref"
> & {
  children: ReactNode;
};

const RowButton = forwardRef<HTMLButtonElement, RowButtonProps>(
  (
    {
      isDisabled,
      onClick,
      id,
      tabIndex,
      "aria-label": ariaLabel,
      "aria-controls": ariaControls,
      "aria-expanded": ariaExpanded,
      children,
    },
    ref,
  ) => (
    <Box
      ref={ref}
      as="button"
      type="button"
      id={id}
      tabIndex={tabIndex}
      aria-label={ariaLabel}
      aria-controls={ariaControls}
      aria-expanded={ariaExpanded}
      disabled={isDisabled}
      onClick={onClick}
      display="flex"
      alignItems="center"
      gap={2}
      width="100%"
      px={3}
      py={2.5}
      bg="base.lightBackground"
      color="primary.text"
      fontWeight="medium"
      textAlign="left"
      transitionProperty="background-color"
      transitionDuration="fast"
      transitionTimingFunction="easeOutQuad"
      _hover={{ bg: "primary.background" }}
      _focusVisible={{ outline: "none", boxShadow: "outline" }}
      _disabled={{ color: "text.disabled", cursor: "not-allowed" }}
    >
      {children}
    </Box>
  ),
);
RowButton.displayName = "RowButton";
